import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface CookiesPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function CookiesPolicyModal({
  isOpen,
  onClose
}: CookiesPolicyModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  return <AnimatePresence data-id="element-18">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-19">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden="true" data-id="element-20" />

          {/* Modal Content */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" role="dialog" aria-modal="true" aria-labelledby="cookies-modal-title" data-id="element-21">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white sticky top-0 z-10" data-id="element-22">
              <h2 id="cookies-modal-title" className="text-xl font-bold text-slate-900" data-id="element-23">
                Política de Cookies
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors" aria-label="Cerrar" data-id="element-24">
                <X size={20} data-id="element-25" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="overflow-y-auto p-6 md:p-8 text-slate-600 space-y-8" data-id="element-26">
              <p className="text-lg font-semibold text-slate-800" data-id="element-27">
                Política de Cookies de TicketMaestro
              </p>

              <section data-id="element-28">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-29">
                  1. INTRODUCCION
                </h3>
                <p className="leading-relaxed" data-id="element-30">
                  En TicketMaestro utilizamos cookies y tecnologías similares
                  para mejorar la experiencia de navegación en nuestros sitios
                  web, analizar el tráfico, personalizar el contenido y mostrar
                  anuncios relevantes. A continuación, te explicamos qué son las
                  cookies, cómo las usamos y cómo puedes gestionarlas.
                </p>
              </section>

              <section data-id="element-31">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-32">
                  2. ¿QUE SON LAS COOKIES?
                </h3>
                <p className="leading-relaxed" data-id="element-33">
                  Las cookies son pequeños archivos de texto que un sitio web
                  almacena en tu dispositivo (computadora, celular o tablet) al
                  visitarlo. Estas permiten que el sitio "recuerde" tus acciones
                  o preferencias (como idioma, inicio de sesión, etc.) durante
                  un período de tiempo determinado.
                </p>
              </section>

              <section data-id="element-34">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-35">
                  3. ¿QUE TIPOS DE COOKIES UTILIZAMOS?
                </h3>
                <div className="space-y-4" data-id="element-36">
                  <div data-id="element-37">
                    <h4 className="font-semibold text-slate-800" data-id="element-38">
                      A. Cookies necesarias (técnicas):
                    </h4>
                    <p className="leading-relaxed" data-id="element-39">
                      Permiten la navegación en el sitio web y el uso de sus
                      funciones esenciales. Sin estas cookies, el sitio no puede
                      funcionar correctamente.
                    </p>
                  </div>
                  <div data-id="element-40">
                    <h4 className="font-semibold text-slate-800" data-id="element-41">
                      B. Cookies de preferencia o personalización:
                    </h4>
                    <p className="leading-relaxed" data-id="element-42">
                      Almacenan información para que el usuario acceda al
                      servicio con características personalizadas, como el
                      idioma o región.
                    </p>
                  </div>
                  <div data-id="element-43">
                    <h4 className="font-semibold text-slate-800" data-id="element-44">
                      C. Cookies de análisis o rendimiento:
                    </h4>
                    <p className="leading-relaxed" data-id="element-45">
                      Nos permiten conocer el comportamiento de los visitantes
                      para mejorar el sitio. Usamos herramientas como Google
                      Analytics u otras similares.
                    </p>
                  </div>
                  <div data-id="element-46">
                    <h4 className="font-semibold text-slate-800" data-id="element-47">
                      D. Cookies de publicidad o marketing:
                    </h4>
                    <p className="leading-relaxed" data-id="element-48">
                      Recopilan información sobre tus hábitos de navegación para
                      mostrarte anuncios relevantes en nuestros sitios o en
                      otros.
                    </p>
                  </div>
                </div>
              </section>

              <section data-id="element-49">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-50">
                  4. ¿CUANTO TIEMPO PERMANECEN ACTIVAS?
                </h3>
                <ul className="list-disc pl-5 space-y-2" data-id="element-51">
                  <li data-id="element-52">
                    <strong data-id="element-53">Cookies de sesión:</strong> se eliminan al cerrar el
                    navegador.
                  </li>
                  <li data-id="element-54">
                    <strong data-id="element-55">Cookies persistentes:</strong> permanecen en tu
                    dispositivo hasta que las elimines o expiren.
                  </li>
                </ul>
              </section>

              <section data-id="element-56">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-57">
                  5. ¿COMO PUEDES CONTROLAR LAS COOKIES?
                </h3>
                <p className="leading-relaxed mb-4" data-id="element-58">
                  Al ingresar por primera vez a nuestro sitio, se te mostrará un
                  aviso de cookies. Puedes aceptar todas, rechazarlas o
                  configurarlas según tu preferencia.
                </p>
                <p className="leading-relaxed mb-2" data-id="element-59">
                  Además, puedes modificar la configuración de cookies desde tu
                  navegador en cualquier momento:
                </p>
                <ul className="list-disc pl-5 space-y-2" data-id="element-60">
                  <li data-id="element-61">Google Chrome: Configuración de cookies</li>
                  <li data-id="element-62">Mozilla Firefox: Administrar cookies</li>
                  <li data-id="element-63">Safari: Soporte de Safari</li>
                  <li data-id="element-64">Microsoft Edge: Privacidad y servicios</li>
                </ul>
              </section>

              <section data-id="element-65">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-66">
                  6. COOKIES DE TERCEROS
                </h3>
                <p className="leading-relaxed mb-4" data-id="element-67">
                  Algunas cookies pueden ser instaladas por servicios de
                  terceros utilizados en nuestro sitio (como redes sociales,
                  plataformas de video, servicios de estadísticas, etc.). No
                  tenemos control directo sobre ellas. Te recomendamos revisar
                  sus respectivas políticas:
                </p>
                <ul className="list-disc pl-5 space-y-2" data-id="element-68">
                  <li data-id="element-69">Google: Política de privacidad</li>
                  <li data-id="element-70">Meta (Facebook / Instagram): Política de cookies</li>
                </ul>
              </section>

              <section data-id="element-71">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-72">
                  7. CAMBIOS EN ESTA POLITICA
                </h3>
                <p className="leading-relaxed" data-id="element-73">
                  Podemos actualizar esta Política de Cookies en cualquier
                  momento. Te notificaremos en nuestro sitio web cuando se hagan
                  cambios relevantes.
                </p>
              </section>

              <section data-id="element-74">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-75">
                  8. CONTACTO
                </h3>
                <p className="leading-relaxed mb-2" data-id="element-76">
                  Si tienes dudas sobre nuestra política de cookies, puedes
                  escribirnos a:
                </p>
                <ul className="list-disc pl-5" data-id="element-77">
                  <li data-id="element-78">
                    <a href="mailto:protecciondatos@ticketmaestro.net" className="text-indigo-600 hover:underline" data-id="element-79">
                      protecciondatos@ticketmaestro.net
                    </a>
                  </li>
                </ul>
              </section>

              <section data-id="element-80">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-81">
                  9. VIGENCIA
                </h3>
                <p className="leading-relaxed" data-id="element-82">
                  La vigencia de estas Políticas es desde el 20 de marzo de 2026
                  y permanecerá vigente hasta que exista una nueva versión que
                  la reemplace.
                </p>
              </section>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end sticky bottom-0 z-10" data-id="element-83">
              <button onClick={onClose} className="px-6 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-medium" data-id="element-84">
                Entendido
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}